import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";

// =========================== EMAIL DELIVERY LOG ===========================
function EmailLogTab() {
  const [data, setData] = useState({ rows: [], counts: {}, total: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [toFilter, setToFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (statusFilter) params.append("status", statusFilter);
      if (toFilter) params.append("to", toFilter);
      const { data: body } = await teacherApi.get(`/admin/email-log?${params}`);
      setData(body);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd ładowania logów");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const statusBadge = (s) => {
    const map = {
      sent:       { bg: "#FEF08A", label: "SENT"       },
      delivered:  { bg: "#BBF7D0", label: "DELIVERED"  },
      opened:     { bg: "#BAE6FD", label: "OPENED"     },
      clicked:    { bg: "#DDD6FE", label: "CLICKED"    },
      bounced:    { bg: "#FECACA", label: "BOUNCED"    },
      complained: { bg: "#FCA5A5", label: "COMPLAINED" },
      failed:     { bg: "#FCA5A5", label: "FAILED"     },
    };
    const cfg = map[s] || { bg: "#E2E8F0", label: (s || "?").toUpperCase() };
    return (
      <span
        className="brut border-2 border-[#0F172A] rounded-md px-2 py-0.5 text-[10px] font-black tracking-wider"
        style={{ background: cfg.bg }}
        data-testid={`email-log-status-${s}`}
      >
        {cfg.label}
      </span>
    );
  };

  const STATUSES = ["sent", "delivered", "opened", "clicked", "bounced", "complained"];

  return (
    <Section icon={FileSearch} title="Logi dostarczenia emaili">
      <p className="text-sm text-[#64748B] mb-4">
        Każdy wysłany email jest logowany z <code>resend_id</code>. Webhook Resend
        (<code>/api/webhooks/resend</code>) aktualizuje status: sent → delivered →
        opened/clicked, albo bounced/complained. Retencja: ostatnie 30 dni.
      </p>

      {/* ---------- Counts by status ---------- */}
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="email-log-counts">
        {STATUSES.map((s) => (
          <div
            key={s}
            className="brut border-2 border-[#0F172A] rounded-md px-3 py-2 bg-white cursor-pointer"
            onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
            data-testid={`email-log-filter-${s}`}
          >
            <div className="text-xl font-black">{data.counts[s] || 0}</div>
            <div className="text-[10px] font-bold uppercase tracking-wider">{s}</div>
          </div>
        ))}
      </div>

      {/* ---------- Filters ---------- */}
      <div className="flex gap-2 mb-4 items-center flex-wrap">
        <input
          type="text"
          placeholder="Filtruj po adresie email…"
          value={toFilter}
          onChange={(e) => setToFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="brut border-2 border-[#0F172A] rounded-md px-3 py-1 text-sm font-mono w-full sm:w-72"
          data-testid="email-log-to-filter"
        />
        {statusFilter && (
          <Button
            size="sm"
            onClick={() => setStatusFilter("")}
            className="brut brut-hover bg-[#FEF08A] font-bold rounded-md whitespace-nowrap"
            data-testid="email-log-clear-filter"
          >
            Wyczyść filtr: {statusFilter}
          </Button>
        )}
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={load}
          disabled={loading}
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="email-log-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={2.5} />
          Odśwież
        </Button>
      </div>

      {/* ---------- Rows ---------- */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="email-log-list">
        {data.rows.length === 0 && (
          <div className="text-sm text-[#64748B] italic">Brak logów spełniających kryteria.</div>
        )}
        {data.rows.map((r) => (
          <div
            key={r.id}
            className="brut border-2 border-[#0F172A] rounded-md p-3 bg-white text-xs font-mono"
            data-testid={`email-log-row-${r.id}`}
          >
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {statusBadge(r.status)}
              <span className="brut border-2 border-[#0F172A] rounded-md px-1.5 py-0.5 text-[9px] font-black bg-[#F1F5F9]">
                {(r.transport || "?").toUpperCase()}
              </span>
              <span className="font-black break-all">{r.to}</span>
              <span className="text-[#64748B] ml-auto">{new Date(r.created_at).toLocaleString("pl")}</span>
            </div>
            <div className="font-bold text-[13px] break-all">{r.subject}</div>
            {r.resend_id && (
              <div className="text-[#475569] mt-1 break-all">Resend ID: <code>{r.resend_id}</code></div>
            )}
            <details className="mt-1">
              <summary className="text-[#475569] cursor-pointer text-[11px]">
                Historia zdarzeń ({(r.events || []).length})
              </summary>
              <ul className="ml-4 text-[10px] mt-1">
                {(r.events || []).map((ev, i) => (
                  <li key={i}>
                    <b>{ev.status}</b> — {ev.ts && new Date(ev.ts).toLocaleString("pl")}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        ))}
      </div>
    </Section>
  );
}


export default EmailLogTab;
